import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Paperclip } from "@phosphor-icons/react";

/** Loads the immutable source documents already on file for one child and one review context. */
export const useSourceDocuments = (studentId, context) => {
  const [docs, setDocs] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/health-documents`, { params: { context } });
      setDocs(data);
    } catch { setDocs([]); }
  }, [studentId, context]);
  useEffect(() => { load(); }, [load]);
  return { docs, reloadDocs: load };
};

const blankDoc = { doc_type: "", issuer: "", signed_date: "", effective_date: "", expires_on: "" };

/**
 * Picks an existing filed document or files a new one. A document is either an uploaded file or a
 * link to a saved form version — never a free-text note on its own.
 */
export const SourceDocumentFields = ({ studentId, context, types, docs, value, onChange, onFiled, testPrefix }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankDoc);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const fileIt = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("context", context);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
    if (file) fd.append("file", file);
    try {
      const { data } = await api.post(`/students/${studentId}/health-documents`, fd);
      toast.success("Source document filed");
      setAdding(false); setForm(blankDoc); setFile(null);
      await onFiled?.();
      onChange(data.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-border p-4 bg-white" data-testid={`${testPrefix}-documents`}>
      <Label className="text-sm font-bold">Source document</Label>
      <p className="text-xs text-muted-foreground mt-1">
        A signed declaration already in an enrollment form counts — no duplicate upload is needed.
      </p>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid={`${testPrefix}-document-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No document linked yet</SelectItem>
          {docs.map((d) => (
            <SelectItem key={d.id} value={d.id}>
              {d.doc_type_label} · {d.issuer} · signed {d.signed_date}{d.expires_on ? ` · expires ${d.expires_on}` : ""} (v{d.version})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!adding ? (
        <Button size="sm" variant="outline" className="mt-3 rounded-full font-bold"
                onClick={() => setAdding(true)} data-testid={`${testPrefix}-add-document`}>
          <Paperclip size={14} className="mr-1" /> File a new document
        </Button>
      ) : (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <div><Label className="text-sm font-bold">Document type</Label>
            <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
              <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`${testPrefix}-doc-type`}><SelectValue placeholder="Choose a type" /></SelectTrigger>
              <SelectContent>{Object.entries(types || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-sm font-bold">Issued or signed by</Label>
              <Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                     className="mt-1 h-11 rounded-xl" data-testid={`${testPrefix}-doc-issuer`} /></div>
            <div><Label className="text-sm font-bold">Signed date</Label>
              <Input type="date" value={form.signed_date} onChange={(e) => setForm({ ...form, signed_date: e.target.value })}
                     className="mt-1 h-11 rounded-xl" data-testid={`${testPrefix}-doc-signed`} /></div>
            <div><Label className="text-sm font-bold">Effective date</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                     className="mt-1 h-11 rounded-xl" data-testid={`${testPrefix}-doc-effective`} /></div>
            <div><Label className="text-sm font-bold">Expires / review date</Label>
              <Input type="date" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })}
                     className="mt-1 h-11 rounded-xl" data-testid={`${testPrefix}-doc-expires`} /></div>
          </div>
          <div><Label className="text-sm font-bold">Attach the signed file</Label>
            <input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="mt-1 block w-full text-sm" data-testid={`${testPrefix}-doc-file`} /></div>
          <div className="flex gap-2">
            <Button size="sm" onClick={fileIt} disabled={busy || !form.doc_type || !form.issuer || !form.signed_date}
                    className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid={`${testPrefix}-doc-save`}>{busy ? "Filing…" : "File document"}</Button>
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
